import useVehicleList from "@modules/vehicle/hooks/useVehicleList";
import PublicLayout from "@layouts/public/components/PublicLayout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation, Trans } from "next-i18next";
import { GetStaticProps, GetStaticPropsContext } from "next";
import Head from "next/head";
import InfiniteScroll from "react-infinite-scroll-component";
import { FilterIcon } from "@talladega/icons";
import { ReactElement, useContext, useEffect, useMemo, useState } from "react";
import useUser from "@modules/user/hooks/useUser";
import SideBar from "@layouts/profile/SideBar";
import VehicleFilterContainer from "@modules/vehicle/components/VehicleFilterContainer";
import VehicleBadgeContainer from "@modules/vehicle/components/VehicleBadgeContainer";
import useFiltersQuerystringSync from "@modules/vehicle/hooks/useFiltersQuerystringSync";
import { dehydrate } from "@tanstack/react-query";
import useGetVehicleFilterData, {
  prefetchVehicleFilterData,
} from "@modules/vehicle/hooks/useVehicleFilterData";
import { getServerSideQueryClient } from "@modules/api/queryClients";
import VehicleNoResults from "@modules/vehicle/components/VehicleNoResults";
import { UserFavouriteVehicle } from "@modules/vehicle/types/UserFavouriteVehicle";
import { useRouter } from "next/router";
import { PromoCardDetails } from "@modules/vehicle/types/vehiclePromotions";
import VehiclePromoCard from "@modules/vehicle/components/VehiclePromoCard";
import getPromotions from "@modules/vehicle/hooks/useVehiclePromotions";
import VehicleCard from "@modules/vehicle/components/VehicleCard";
import VehicleList from "@modules/vehicle/types/vehicleList";
import VehicleFilterContext from "@modules/vehicle/VehicleFilterContext";
import {
  FilterActions,
  FilterKeys,
  MakeFilter,
} from "@modules/vehicle/types/vehicleFilter";
import VehicleCardListSkeleton from "@common/components/loader/VehicleCardListSkeleton";
import useLocalStorage from "@common/hooks/useLocalStorage";
import LoginModalContext from "@modules/user/LoginModalContext";
import { LoadingSpinner, Button } from "@talladega/components";
import LocationButtonDetailed from "@modules/location/components/LocationButtonDetailed";
import { isTransitionComplete } from "@common/helpers/modalHelper";
import SeoInfo from "@common/components/SeoInfo";
import useSeoVehicleFilters from "@common/hooks/useSeoVehicleFilters";
import { gtmPushData } from "@common/helpers/gtm";
import { loginMode } from "@modules/user/types/LoginModalMode";
import { Event as HotjarEvent, hotjar } from "@modules/core/hotjar/types";
import canVehicleFilterBeBadged from "@modules/vehicle/helpers/canVehicleFilterBeBadged";
import { DynamicSaveSearchModal } from "@modules/vehicle/components";
import { useDevice, useWindowDimensions } from "@talladega/hooks";
import getVlpTourSteps from "@modules/tour/helpers/getVlpTourSteps";
import useTour from "@modules/tour/hooks/useTour";
import convertSearchFiltersToFilterState from "@modules/vehicle/helpers/convertSearchFiltersToFilterState";
import { sizeClass } from "@modules/cms/types/SizeClass";
import useClientSide from "@common/hooks/useClientSide";
import { newArrivals } from "@modules/vehicle/components/NewArrivalImage";
import getVehicleCardPromoBanner from "@modules/vehicle/hooks/useVehicleCardPromoBanner";
import { VehicleCardPromoBanner } from "@modules/vehicle/types/VehicleCardPromoBanner";
import getSRPBanner from "@modules/vehicle/hooks/useSRPBanner";
import SRPBanner from "@modules/vehicle/components/SRPBanner";
import { SRPBannerType } from "@modules/vehicle/types/SRPBannerType";
import GravityFormsPopupModal from "@modules/cms/components/GravityFormsPopupModal";
import GravityFormsPopupModalEnum from "@modules/cms/constants/GravityFormsPopupModalEnum";
import getVehicleCardCTA from "@modules/vehicle/hooks/useVehicleCardCTA";
import { VehicleCardCTAType } from "@modules/vehicle/types/VehicleCardCTAType";
import generateRandomKey from "@common/helpers/generateRandomKey";

type Props = {
  vehiclePromotions: PromoCardDetails[];
  vehicleCardPromoBanner: VehicleCardPromoBanner | null;
  srpBanner: SRPBannerType | null;
  vehicleCardCTA: VehicleCardCTAType | null;
};

type Card =
  | {
      type: "promo";
      value: PromoCardDetails;
      id: string;
    }
  | {
      type: "vehicle";
      value: VehicleList;
      id: string;
      favourite: UserFavouriteVehicle | undefined;
    };

const modalTransitionDuration = 201;
const defaultPageSize = 24;
const mobilePageSize = 8;

const VehiclesPage = ({
  vehiclePromotions,
  vehicleCardPromoBanner = null,
  srpBanner = null,
  vehicleCardCTA = null,
}: Props) => {
  const { t } = useTranslation([
    "common",
    "vehicle",
    "vehiclesFilters",
    "vehiclesPage",
  ]);
  const { vehicleFilters, dispatch, showVehicleFilterState } =
    useContext(VehicleFilterContext);
  const { locale } = useRouter();
  const { user } = useUser();
  const { vehicleFilters: vehicleFiltersData } = useGetVehicleFilterData();
  const [isFavouriteVehiclesCached, setIsFavouriteVehiclesCached] =
    useState<boolean>(false);
  const [favouriteVehiclesCopy, setFavouriteVehiclesCopy] =
    useState<UserFavouriteVehicle[]>();
  const [showFilters, setShowFilters] = showVehicleFilterState;
  const {
    setShowModal: setShowLoginModal,
    modeState: [, setLoginModalModeState],
  } = useContext(LoginModalContext);
  const device = useDevice();

  const savedVehicleFilter = vehicleFilters.filters.find(
    (f) => f.key === FilterKeys.SavedVehicles
  );

  const { isSynced } = useFiltersQuerystringSync(
    vehicleFilters.filters,
    dispatch
  );

  const {
    data,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
  } = useVehicleList(
    {
      pageSize: device?.isMobile ? mobilePageSize : defaultPageSize,
      vehicleFilters,
      locale: locale || "",
      favouriteVehicles: favouriteVehiclesCopy || [],
      enabled: isSynced,
    },
    vehicleFiltersData,
    true
  );

  useEffect(() => {
    const matchedFiltersFromQuery = data?.pages[0].isRecommended
      ? data?.pages[0].unmatchedFilters
      : data?.pages[0].matchedFilters;

    const filtersStateFromQuery =
      vehicleFiltersData && matchedFiltersFromQuery
        ? convertSearchFiltersToFilterState(
            matchedFiltersFromQuery,
            vehicleFiltersData
          )
        : undefined;

    // update filters state after we get back the matched filters from useVehicleList
    if (filtersStateFromQuery && vehicleFilters.isDirty) {
      dispatch({
        type: FilterActions.SetVehicleFiltersOnly,
        payload: filtersStateFromQuery,
      });
    }
  }, [data?.pages, dispatch, vehicleFilters.isDirty, vehicleFiltersData]);

  const isRecommended = data?.pages[0]?.isRecommended;
  const hasRightPaymentTradeInValue =
    data?.pages[0]?.hasPersonalTradeInValue ?? false;
  const hasRightPaymentInterestRate =
    data?.pages[0]?.hasPersonalInterestRate ?? false;
  const vehicleHasPrice =
    data?.pages[0]?.vehicles[0]?.pricing?.listingPrice !== 0 ?? false;
  const vehicleList = data?.pages.flatMap((p) => p.vehicles) || [];
  const totalItems = isRecommended ? 0 : data?.pages[0].totalItems;
  const paymentFactors = data?.pages[0]?.paymentFactors;

  const [showVehicleSwipe, setShowVehicleSwipe] = useState<boolean>(false);
  const [vehicleSwipeSeen, setVehicleSwipeSeen] =
    useLocalStorage<boolean>("vehicleSwipeSeen");
  const [showSaveSearchAfterLogin, setShowSaveSearchAfterLogin] =
    useLocalStorage<boolean>("showSaveSearchAfterLogin");

  const [vlpTradeInTourSeen, setVlpTradeInTourSeen] =
    useLocalStorage<boolean>("vlpTradeInTourSeen");
  const [vlpCreditTourSeen, setVlpCreditTourSeen] =
    useLocalStorage<boolean>("vlpCreditTourSeen");

  const showTradeInTour =
    !vlpTradeInTourSeen && hasRightPaymentTradeInValue && vehicleHasPrice;
  const showCreditTour =
    !vlpCreditTourSeen && hasRightPaymentInterestRate && vehicleHasPrice;

  const [isFirstVehicleTooltipOpen, setIsFirstVehicleTooltipOpen] =
    useState<boolean>(false);

  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);

  let currentPromoCardIndex = 0;

  const [scrollableDivKey, setScrollableDivKey] = useState(generateRandomKey());
  const [vlpHeading, setVlpHeading] = useState<string>(
    t("common:used_cars_for_sale")
  );

  const seoInfoWithFilters = useSeoVehicleFilters(
    vehicleFilters.filters,
    locale || ""
  );

  const isClientSide = useClientSide();
  const { width } = useWindowDimensions();
  /**
   * Tablet can rotate and the width can be bigger than 1024.
   * In this case, the Sidebar is not collapsed anymore
   * and, we only want a mobile tour when the sidebar is collapsed
   */
  const isMobileTour = width < sizeClass.lg;
  const isSmallScreen = isClientSide && width < 460;
  const [isTourActive, setIsTourActive] = useState(false);

  const { steps, vlpTourStepsIndex } = useMemo(
    () => getVlpTourSteps(showCreditTour, showTradeInTour, isMobileTour, t),
    [isMobileTour, showCreditTour, showTradeInTour, t]
  );

  const canStartTour =
    typeof window !== "undefined" &&
    !isLoading &&
    !isFetching &&
    (showTradeInTour || showCreditTour);

  const { tour } = useTour({
    renderSteps: canStartTour,
    steps,
  });

  tour
    ?.onbeforechange(async () => {
      if (
        showCreditTour &&
        tour.currentStep() === vlpTourStepsIndex.interestRateTooltip
      ) {
        setIsFirstVehicleTooltipOpen(true);
        // wait until tooltip showed up e.g., not hidden
        return new Promise((resolveTooltipRendered) => {
          const appearInterval = setInterval(() => {
            const rightPaymentTooltip = document.querySelector(
              ".right-payment-tooltip-tour"
            );
            if (!rightPaymentTooltip?.classList.contains("hidden")) {
              clearInterval(appearInterval);
              resolveTooltipRendered(true);
            }
          }, 200);
        });
      }

      if (
        showTradeInTour &&
        tour.currentStep() === vlpTourStepsIndex.tradeInInput
      ) {
        // for mobile, we need to expand the Sidebar first
        if (isMobileTour) {
          setShowFilters(true);
          // since we are using headless-ui portal for rendering mobile sidebar
          // we need to wait until it is in the DOM
          await new Promise((resolveSidebarOpened) => {
            const sidebarShownInterval: NodeJS.Timer = setInterval(() => {
              const sidebar = document.querySelector("#mobile-sidebar");
              if (sidebar) {
                clearInterval(sidebarShownInterval);
                resolveSidebarOpened(true);
              }
            }, 200);
          });
          // wait until Sidebar fully expanded
          await new Promise((resolveTransitionFinished) => {
            setTimeout(resolveTransitionFinished, 100);
          });
          /** TODO: Remove @ts-ignore when @types/intro.js is updated.
           * confirmed in intro.js source code that scrollTo is an actual option */
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          tour.refresh(true);
          document.querySelector("#mobile-sidebar")?.scroll({ top: 300 });
        }
      }

      return true;
    })
    .onafterchange(() => {
      if (
        showCreditTour &&
        tour.currentStep() === vlpTourStepsIndex.interestRateTooltip
      ) {
        setShowFilters(false);
      }

      if (
        showTradeInTour &&
        tour.currentStep() === vlpTourStepsIndex.vehicleCardRightPayment
      ) {
        setShowFilters(false);
        setIsFirstVehicleTooltipOpen(false);
      }

      if (
        showTradeInTour &&
        tour.currentStep() === vlpTourStepsIndex.tradeInInput
      ) {
        setIsFirstVehicleTooltipOpen(false);
      }
    })
    .onstart(() => {
      setIsTourActive(true);
    })
    .oncomplete(() => {
      if (showTradeInTour) {
        setVlpTradeInTourSeen(true);
      }

      if (showCreditTour) {
        setVlpCreditTourSeen(true);
      }

      setIsTourActive(false);
    })
    .onexit(() => {
      setIsFirstVehicleTooltipOpen(false);
      setShowFilters(false);
    });

  useEffect(() => {
    if (vehicleFilters.isDirty) {
      window.scrollTo({ top: 0, left: 0 });

      /** add a unique key to the div so that when vehicleFilters are added, it resets the scrollable component avoiding the api/vehicles
       *  requests bombardment as infinite scroll affects the number of data fetched */
      setScrollableDivKey(generateRandomKey());
    }
  }, [vehicleFilters]);

  useEffect(() => {
    if (
      savedVehicleFilter?.state &&
      !isFavouriteVehiclesCached &&
      user?.favouriteVehicles.length
    ) {
      setIsFavouriteVehiclesCached(true);
      setFavouriteVehiclesCopy(user?.favouriteVehicles);
      return;
    }

    setIsFavouriteVehiclesCached(!!savedVehicleFilter?.state);
  }, [
    isFavouriteVehiclesCached,
    savedVehicleFilter?.state,
    user?.favouriteVehicles,
  ]);

  useEffect(() => {
    if (!isFetching && data?.pages[0]?.vehicles.length && !vehicleSwipeSeen) {
      setVehicleSwipeSeen(true);
      setShowVehicleSwipe(true);
    }
  }, [setVehicleSwipeSeen, isFetching, data, vehicleSwipeSeen]);

  const [totalSearchEvents, setTotalSearchEvents] = useState<number>(0);
  const memoizedVehicleList = useMemo(
    () => data?.pages.flatMap((p) => p.vehicles) ?? [],
    [data?.pages]
  );
  useEffect(() => {
    if (
      vehicleFilters.filters.length > 0 &&
      data?.pages.length &&
      totalSearchEvents < data?.pages.length &&
      !isFetching
    ) {
      setTotalSearchEvents((count) => count + 1);
      const vins = memoizedVehicleList.map((v) => v.vin);
      const stockNumbers = memoizedVehicleList.map((v) => ({
        id: v.stockNumber,
        google_business_vertical: "custom",
      }));
      if (vins.length > 0 && stockNumbers.length > 0) {
        gtmPushData({
          event: "Search",
          content_type: "vehicle",
          content_ids: vins,
          items: stockNumbers,
          state_of_vehicle: undefined,
          country: undefined,
          vehicleYear: undefined,
          vehicleMake: undefined,
          vehicleModel: undefined,
          vehicleStockNumber: undefined,
          vehicleBodyType: undefined,
          vehicleExteriorColor: undefined,
          vehicleTransmission: undefined,
          vehicleFuelType: undefined,
          vehicleDrivetrain: undefined,
          vehiclePrice: undefined,
          vehicleHasSpinUrl: undefined,
          currency: undefined,
        });
      }
    } else if (vehicleFilters.filters.length === 0 || vehicleFilters.isDirty) {
      setTotalSearchEvents(0);
    }
  }, [
    vehicleFilters.filters,
    vehicleFilters.isDirty,
    memoizedVehicleList,
    data?.pages.length,
    totalSearchEvents,
    setTotalSearchEvents,
    isFetching,
  ]);

  const makeAndBodyFilters = useMemo(() => {
    // Convert model filters into its corresponding make filter type.
    const filters = vehicleFilters.filters
      .flatMap((vf) => {
        if (vf.key === FilterKeys.Model) {
          const makeFilter: MakeFilter = {
            key: FilterKeys.Make,
            state: { make: vf.state.make },
          };
          return makeFilter;
        }
        return vf;
      })
      .filter(
        (f) => f.key === FilterKeys.Make || f.key === FilterKeys.BodyStyle
      );

    // Remove duplicate makes as a result of model type conversion.
    const result = filters.filter((f, index) => {
      if (f.key === FilterKeys.Make) {
        return (
          filters.findIndex(
            (e) => e.key === FilterKeys.Make && e.state.make === f.state.make
          ) === index
        );
      }
      return f;
    });

    return result;
  }, [vehicleFilters.filters]);

  const makeAndBodyFiltersToString = useSeoVehicleFilters(
    makeAndBodyFilters,
    locale || ""
  );

  useEffect(() => {
    if (isLoading) return;

    if (!makeAndBodyFiltersToString) {
      setVlpHeading(t("common:used_cars_for_sale"));
    } else if (!totalItems || totalItems === 0) {
      setVlpHeading(t("common:no_search_results"));
    } else {
      setVlpHeading(`${t("common:used")} ${makeAndBodyFiltersToString}`);
    }
  }, [makeAndBodyFiltersToString, isLoading, t, totalItems]);

  const showFiltersOnClick = () => {
    setShowFilters(true);
  };

  const hideFiltersOnClick = () => {
    setShowFilters(false);
  };

  const addPromoCard = (index: number) => {
    const lowCardSkip = 6;
    const highCardSkip = 14;
    const chunkIndex = index % highCardSkip;

    if (chunkIndex === lowCardSkip || (chunkIndex === 0 && index !== 0)) {
      if (typeof vehiclePromotions !== "undefined") {
        const promoCardDetails = vehiclePromotions[currentPromoCardIndex];

        if (currentPromoCardIndex === vehiclePromotions.length - 1) {
          currentPromoCardIndex = 0;
        } else {
          currentPromoCardIndex += 1;
        }

        return promoCardDetails;
      }
    }
    return null;
  };

  const vehicleAndPromoCards: Card[] = [];
  const favourites = user?.favouriteVehicles || [];

  vehicleList.forEach((vehicle, index) => {
    const promoCard = addPromoCard(index);
    if (promoCard) {
      vehicleAndPromoCards.push({
        type: "promo",
        value: promoCard,
        id: promoCard.cardHeading + index.toString(),
      });
    }

    vehicleAndPromoCards.push({
      type: "vehicle",
      value: vehicle,
      id: vehicle.id,
      favourite: favourites?.find((f) => f.vehicleId === vehicle.id),
    });
  });

  const favouriteRemoved = () => {
    const favCount = user?.favouriteVehicles?.length;

    // Remove filter when there are no favourites
    if (favCount === 1) {
      dispatch({
        type: FilterActions.SetSavedVehiclesFilter,
        payload: false,
      });
    }
  };

  const renderVehicleCards = () => {
    let cards = vehicleAndPromoCards;

    if (isRecommended) {
      cards = cards.slice(0, 20);
    }

    return cards.map((card, index) => {
      if (card.type === "promo") {
        return <VehiclePromoCard vehiclePromotion={card.value} key={card.id} />;
      }

      return (
        <VehicleCard
          isRightPaymentEligible={
            hasRightPaymentTradeInValue || hasRightPaymentInterestRate
          }
          tooltipDefaultOpen={isFirstVehicleTooltipOpen}
          key={card.id}
          vehicle={card.value}
          index={index}
          showSwipeAnimation={index === 0 && showVehicleSwipe}
          onFavouriteRemoved={() => favouriteRemoved()}
          vehicleFilters={vehicleFilters}
          isTourActive={isTourActive}
          vehicleCardPromoBanner={vehicleCardPromoBanner}
          vehicleCardCTA={vehicleCardCTA}
        />
      );
    });
  };

  const saveSearch = () => {
    if (!user?.isLoggedIn) {
      setShowSaveSearchAfterLogin(true);
      setLoginModalModeState(loginMode);
      setShowLoginModal(true);
    } else {
      hotjar.event(HotjarEvent.SaveSearch);
      setShowSaveSearchModal(true);
    }
  };

  const handleSaveSearch = () => {
    gtmPushData({
      event: "Filter",
      element: "Save-Search",
      descriptor: "Save-Your-Search",
    });
    setShowFilters(false);
    setTimeout(
      () => isTransitionComplete(() => saveSearch()),
      modalTransitionDuration
    );
  };

  const handleSaveSearchAuthError = () => {
    setShowSaveSearchModal(false);
    setShowFilters(false);
    setShowLoginModal(true);
  };

  const onGetNotifiedClick = () => {
    saveSearch();
  };

  const displayableFilters = vehicleFilters.filters.filter((vf) =>
    canVehicleFilterBeBadged(vf)
  );

  if (
    showSaveSearchAfterLogin &&
    user?.isLoggedIn &&
    vehicleFilters.filters.length > 0
  ) {
    setShowSaveSearchAfterLogin(false);
    saveSearch();
  }

  const handleSaveSearchModalClose = () => {
    gtmPushData({
      event: "Filter",
      element: "Save-Search",
      descriptor: "Close",
    });

    setShowSaveSearchModal(false);
  };

  const getOpengraphImage = () => {
    let vehicleWithImages = null;
    if (vehicleList && vehicleList.length > 0) {
      vehicleWithImages = vehicleList.find(
        (vehicle) => Array.isArray(vehicle.images) && vehicle.images.length > 1
      );
    }
    return vehicleWithImages
      ? vehicleWithImages.images[0].img
      : newArrivals[0].image.VLP;
  };

  return (
    <>
      <Head>
        <link rel="preconnect" href="https://content.homenetiol.com" />
        <link rel="dns-prefetch" href="https://content.homenetiol.com" />
      </Head>
      {seoInfoWithFilters && (
        <SeoInfo
          seoInfo={{
            title: t("vehiclesPage:vlp_title_tag_with_filters", {
              filters: seoInfoWithFilters,
            }),
            metaDesc: t("vehiclesPage:vlp_meta_description_with_filters", {
              filters: seoInfoWithFilters,
            }),
            opengraphTitle: t("vehiclesPage:vlp_og_title__with_filters", {
              filters: seoInfoWithFilters,
            }),
            opengraphType: "website",
            opengraphDescription: t(
              `vehiclesPage:vlp_og_description_with_filters`,
              {
                filters: seoInfoWithFilters,
              }
            ),

            opengraphImage: {
              sourceUrl: getOpengraphImage(),
            },
          }}
        />
      )}
      {!seoInfoWithFilters && (
        <SeoInfo
          seoInfo={{
            title: t("vehiclesPage:vlp_title_tag"),
            metaDesc: t("vehiclesPage:vlp_meta_description"),
            opengraphTitle: t("vehiclesPage:vlp_og_title"),
            opengraphType: "website",
            opengraphDescription: t("vehiclesPage:vlp_og_description"),
            opengraphImage: {
              sourceUrl: getOpengraphImage(),
            },
          }}
        />
      )}
      {user && (
        <GravityFormsPopupModal
          origin={GravityFormsPopupModalEnum.VLP}
          user={user}
        />
      )}

      <div className="bg-white">
        <div id="rightride-content" className="bg-gray-25">
          {width && width < sizeClass.lg && (
            <SRPBanner banner={srpBanner} isMobile />
          )}
          <div className="lg:hidden w-full mx-auto grid grid-cols-1 xs:grid-cols-2 gap-2 md:gap-10 py-2 px-4 sm:px-6 lg:px-8 border-t-2 border-gray-25">
            <div className="grid">
              <Button
                buttonStyle="white"
                onClick={showFiltersOnClick}
                className="text-primary-bold"
                spacing="tight-hug"
                leftIcon={<FilterIcon className="h-5 w-5" />}
                rightIcon={
                  <div>
                    {displayableFilters.length > 0 && (
                      <div className="h-5 min-w-[1.26rem] px-1.5 rounded-full bg-secondary-salmon text-white body-1">
                        <span>{displayableFilters.length}</span>
                      </div>
                    )}
                  </div>
                }
              >
                <div className="text-black font-medium text-sm sm:body-3">
                  {t("vehiclesFilters:filter_and_sort")}
                </div>
              </Button>
            </div>
            <div className="grid [&>button]:px-3 sm:[&>button]:body-3 md:ml-4 md:[&>button]:px-5">
              <LocationButtonDetailed />
            </div>
          </div>
          <div className="flex mx-auto py-2.5 xs:pl-6 xs:pr-6 lg:pl-0 lg:pr-8">
            <div className="h-screen sticky top-0 bg-white rounded-tr-2xl lg:w-1/2 xl:w-1/3 2xl:w-1/4">
              <SideBar
                onHideClick={hideFiltersOnClick}
                showSideBar={showFilters}
              >
                <VehicleFilterContainer
                  onHideClick={hideFiltersOnClick}
                  totalVehicles={totalItems}
                  vehicleFilters={vehicleFilters.filters}
                  onFilterUpdate={dispatch}
                  favouriteVehicles={user?.favouriteVehicles}
                  onSaveSearchClick={() => handleSaveSearch()}
                  locale={locale}
                  paymentFactors={paymentFactors}
                  expandPricingFilter={showTradeInTour}
                />
              </SideBar>
            </div>

            <div className="w-full pb-6 lg:pl-6">
              {width && width > sizeClass.lg && (
                <SRPBanner banner={srpBanner} isMobile={false} />
              )}
              <InfiniteScroll
                style={{
                  overflow: "visible",
                }}
                scrollableTarget="scrollable-container"
                dataLength={vehicleList.length}
                next={fetchNextPage}
                hasMore={hasNextPage || false}
                loader={
                  isFetchingNextPage &&
                  !isRecommended && (
                    <LoadingSpinner isLoading={isFetching} isOverlay={false} />
                  )
                }
              >
                <div
                  className="flex items-center pt-1 lg:pt-4 pb-4"
                  key={`scrollable-div-${scrollableDivKey}`}
                >
                  <div className="flex flex-col mx-3 xs:mx-0">
                    <h1 className="flex-auto text-black text-2xl leading-6 font-semi-bold pr-1">
                      {vlpHeading}
                    </h1>

                    <div className="lg:hidden flex flex-row justify-start mt-1 items-center text-center">
                      <h3 className="text-base text-primary-100">
                        <Trans
                          t={t}
                          i18nKey={
                            isSmallScreen
                              ? "common:count_matches"
                              : "common:count_matches_found"
                          }
                          values={{ count: totalItems }}
                          components={{
                            1: <span className="font-semi-bold" />,
                            2: <span className="font-medium text-gray-600" />,
                          }}
                        />
                      </h3>
                      {displayableFilters.length > 0 && (
                        <>
                          <Button
                            fill="link"
                            spacing="no-padding"
                            className="font-semibold underline"
                            onClick={() => {
                              dispatch({
                                type: FilterActions.ResetFilters,
                                payload: undefined,
                              });
                            }}
                          >
                            {isSmallScreen
                              ? t("common:clear_filters")
                              : t("common:clear_all_filters")}
                          </Button>
                          <span className="text-base text-primary-100 font-medium text-gray-600">
                            {t("common:or")}
                          </span>
                          <Button
                            fill="link"
                            spacing="no-padding"
                            className="font-semibold underline"
                            onClick={() => setShowSaveSearchModal(true)}
                          >
                            {t("vehiclesFilters:save_search")}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="hidden lg:flex items-center ml-auto text-base text-black space-x-1">
                    <Trans
                      t={t}
                      i18nKey="common:count_matches_found"
                      values={{ count: totalItems }}
                      components={{
                        1: <span className="body-3" />,
                        2: <span className="body-4" />,
                      }}
                    />
                  </div>

                  <div className="hidden lg:flex order-last ml-4 [&>button]:px-4">
                    <LocationButtonDetailed />
                  </div>
                </div>
                {!isFetching && (
                  <div className="main-filter-badge-container lg:inline xs:hidden">
                    <VehicleBadgeContainer
                      onFilterUpdate={dispatch}
                      vehicleFilters={vehicleFilters.filters}
                    />
                  </div>
                )}
                {isError && <div>Failed to load vehicles</div>}
                {isFetching && <VehicleCardListSkeleton numberOfCards={20} />}
                {!isFetching && !isError && isRecommended && (
                  <VehicleNoResults onGetNotifiedClick={onGetNotifiedClick} />
                )}
                {!isFetching &&
                  !isError &&
                  isRecommended &&
                  data?.pages[0].totalItems !== 0 && (
                    <h3 className="mt-20 text-primary-bold">
                      {t("common:recommended_vehicles")}
                    </h3>
                  )}
                {!isError && !isLoading && (
                  <div className="lg:mt-6 grid grid-cols-1 gap-y-10 gap-x-6 sm:grid-cols-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5 xl:gap-x-8 grid-flow-row">
                    {renderVehicleCards()}
                  </div>
                )}
              </InfiniteScroll>
            </div>
          </div>
        </div>
        <div className="bg-white w-full py-5 px-4">
          <p className="caption-2">{t("common:disclaimer")}</p>
        </div>
        {showSaveSearchModal && (
          <DynamicSaveSearchModal
            showModal={showSaveSearchModal}
            handleModalClose={handleSaveSearchModalClose}
            vehicleFilters={vehicleFilters.filters}
            onSaveSearchAuthError={() => handleSaveSearchAuthError()}
          />
        )}
      </div>
    </>
  );
};

VehiclesPage.pageName = "VehiclesPage";
VehiclesPage.getLayout = (page: ReactElement) => (
  <PublicLayout>{page}</PublicLayout>
);

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export const getStaticProps: GetStaticProps = async (
  context: GetStaticPropsContext
) => {
  const isPreview = context.preview ?? false;
  const queryClient = getServerSideQueryClient();
  const promos = await getPromotions(
    isPreview,
    context.locale?.toUpperCase() as string
  );

  const vehicleCardPromoBanner = await getVehicleCardPromoBanner(
    isPreview,
    context.locale?.toUpperCase() as string
  );

  const srpBanner = await getSRPBanner(
    isPreview,
    context.locale?.toUpperCase() as string
  );

  const vehicleCardCTA = await getVehicleCardCTA(
    isPreview,
    context.locale?.toUpperCase() as string
  );

  await prefetchVehicleFilterData(queryClient);

  return {
    props: {
      ...(await serverSideTranslations(context.locale ?? "en", [
        "common",
        "common-components",
        "vehicle",
        "vehiclesFilters",
        "vehiclesPage",
      ])),
      dehydratedState: dehydrate(queryClient),
      vehiclePromotions: promos ?? [],
      vehicleCardPromoBanner,
      srpBanner,
      vehicleCardCTA,
    },
    revalidate: 60 * 60,
  };
};

export default VehiclesPage;
